import { HttpMethod } from '@vision/ui/enums';
import type { ApiResponse } from '@vision/ui/interfaces';
import { BenchmarkSetupsService } from '@vision/ui/services/benchmark-setups.service';
import type { CatalogResponseDto, ComparisonResponseDto, MeasureResponseDto } from './benchmark-comparison.dto';
import { fromCatalog, fromComparison, fromMeasure, toComparison } from './benchmark-comparison.mapper';
import type { MatrixCatalog, MatrixMeasure, MatrixRequest, MatrixRowResponse } from './types';

type Scope = { accountId: string; setupId: string };
const base = ({ accountId, setupId }: Scope) =>
  `/v2/accounts/${encodeURIComponent(accountId)}/reporting/benchmark_setups/${encodeURIComponent(setupId)}`;
const extraOptions = { notification: { autoHandleError: false, autoHandleSuccess: false } };

// Discovery and comparisons are measure-scoped. Keep the number of simultaneous requests bounded.
export async function inBatches<T, R>(items: T[], run: (item: T) => Promise<R>, signal: AbortSignal): Promise<R[]> {
  const results: R[] = [];
  for (let offset = 0; offset < items.length && !signal.aborted; offset += 3) {
    results.push(...(await Promise.all(items.slice(offset, offset + 3).map(run))));
  }
  return results;
}

export const BenchmarkComparisonService = BenchmarkSetupsService.injectEndpoints({
  endpoints: (builder) => ({
    matrixMeasures: builder.query<MatrixMeasure[], Scope>({
      query: (scope) => ({ url: `${base(scope)}/benchmark_measures`, method: HttpMethod.GET, extraOptions }),
      transformResponse: (response: ApiResponse<MeasureResponseDto[]>) =>
        response.data.map(fromMeasure).sort((a, b) => a.order - b.order),
    }),
    matrixCatalogs: builder.query<MatrixCatalog[], Scope & { measureIds: string[] }>({
      async queryFn(args, api, _options, baseQuery) {
        const data = await inBatches(
          args.measureIds,
          async (measureId): Promise<MatrixCatalog> => {
            const response = await baseQuery({
              url: `${base(args)}/benchmark_measures/${encodeURIComponent(measureId)}/comparison_options`,
              method: HttpMethod.GET,
              extraOptions,
            });
            if (response.error) return { measureId, pairs: [], levels: [], truncated: false, failed: true };
            return fromCatalog((response.data as ApiResponse<CatalogResponseDto>).data);
          },
          api.signal,
        );
        return { data };
      },
    }),
    matrixComparisons: builder.query<
      MatrixRowResponse[],
      Scope & { rows: MatrixRequest[]; dimensionValues: Record<string, string> }
    >({
      async queryFn(args, api, _options, baseQuery) {
        const data = await inBatches(
          args.rows,
          async (row): Promise<MatrixRowResponse> => {
            const response = await baseQuery({
              url: `${base(args)}/benchmark_comparisons`,
              method: HttpMethod.POST,
              data: toComparison(row, args.dimensionValues),
              extraOptions,
            });
            if (response.error) return { measureId: row.measureId, failed: true };
            return {
              measureId: row.measureId,
              result: fromComparison((response.data as ApiResponse<ComparisonResponseDto>).data),
            };
          },
          api.signal,
        );
        return { data };
      },
    }),
  }),
});
export const { useMatrixMeasuresQuery, useMatrixCatalogsQuery, useLazyMatrixCatalogsQuery, useMatrixComparisonsQuery } =
  BenchmarkComparisonService;
